import React, { useState, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Papa from 'papaparse';

import Box from '@mui/material/Box';
import MuiIconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import FilterIcon from '@mui/icons-material/FilterListOutlined';
import FiltersIcon from '@mui/icons-material/FilterAltOutlined';
import CloseIcon from '@mui/icons-material/Close';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import ProceduresTable from './ProceduresTable';
import ProceduresTableMobile from './ProceduresTableMobile';
import FilterProceduresModal from './FilterProceduresModal';
import ProcedureModal from './ProcedureModal';
import ConfirmationModal from '../modals/ConfirmationModal';
import LoadScreen from '../load-screen';
import Alert from '../shared/Alert';
import Search from '../shared/Search';
import Button from '../shared/Button';
import IconButton from '../shared/IconButton';

import {
  fetchProcedures,
  updateProcedure,
  deleteProcedure,
  createProcedure,
} from '../../actions/proceduresActions';
import { toggleSidebar } from '../../actions/sidebarActions';

import { getComparator, stableSort } from '../../util/table';

import './procedures.scss';

const ProceduresTableContainer = (props) => {
  const {
    procedures = [],
    distributors,
    createProcedure,
    updateProcedure,
    deleteProcedure,
    fetchProcedures,
    withButton,
    toggleSidebar,
    countries,
    products = [],
    documents = [],
  } = props;

  const theme = useTheme();

  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [showCreateModal, toggleCreateModal] = useState(false);
  const [showUpdateModal, toggleUpdateModal] = useState(false);
  const [showDeleteModal, toggleDeleteModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [selectedProcedure, setSelectedProcedure] = useState(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');

  const [showFilterModal, toggleFilterModal] = useState(false);
  const [filterState, setFilterState] = useState({ countries: [], flows: [] });

  const [missingProducts, setMissingProducts] = useState(null);

  const onEditClick = (procedure) => {
    setSelectedProcedure(procedure);
    toggleUpdateModal(true);
  };

  const onProcedureCreate = async (formObj) => {
    try {
      setLoading(true);

      await createProcedure(formObj);
      await fetchProcedures();
      setSuccessMessage('New procedure successfully created');
      toggleCreateModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onProcedureUpdate = async (formObj) => {
    if (selectedProcedure) {
      try {
        setLoading(true);

        await updateProcedure(selectedProcedure?.id, formObj);
        await fetchProcedures();
        setSuccessMessage('Procedure successfully updated');
        toggleUpdateModal(false);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const onDeleteClick = async (id) => {
    setSelectedId(id);
    toggleDeleteModal(true);
  };

  const onDeleteProcedure = async () => {
    if (selectedId) {
      try {
        setDeleteLoading(true)
        await deleteProcedure(selectedId);
        await fetchProcedures();
        toggleDeleteModal(false);
        setSelectedId(null);
      } catch (err) {
        console.error(err);
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  const onUploadProducts = (files) => {
    Papa.parse(files[0], {
      header: false,
      skipEmptyLines: true,
      complete: async (results) => {
        if (results && results.data && selectedProcedure) {
          setMissingProducts([]);
          const foundProducts = [];
          const notFoundProducts = [];
          results?.data?.forEach((product) => {
            const sku = product[0] ? product[0].trim() : '';
            const description = product[1] ? product[1].trim() : '';
            const _product = products?.find((p) => p.sku === sku);
            if (_product) {
              foundProducts?.push(_product);
            } else {
              notFoundProducts?.push(`${sku} - ${description}`);
            }
          });

          if (notFoundProducts?.length) {
            setMissingProducts(notFoundProducts);
          } else {
            const products = selectedProcedure?.products?.slice();

            foundProducts?.forEach((product) => {
              if (!products?.find((p) => p.id === product.id)) {
                products.push(product);
              }
            });

            setLoading(true);

            await updateProcedure(selectedProcedure?.id, { ...selectedProcedure, products });
            await fetchProcedures();

            setSelectedProcedure({ ...selectedProcedure, products });
            setLoading(false);
          }
        }
      },
    });
  };

  const onSubmitFilters = (filters) => {
    setFilterState({
      countries: filters.countries || [],
      flows: filters.flows || [],
    });
    toggleFilterModal(false);
  };

  const onRemoveFilterItem = (key, value) => {
    setFilterState({
      ...filterState,
      [key]: filterState[key].filter((v) => v !== value),
    })
  }

  const filteredProcedures = useMemo(() => {
    let filtered = procedures;

    if (search) {
      const q = search.toLowerCase();
      filtered = procedures.filter((p) => p?.name?.toLowerCase()?.includes(q));
    }

    if (filterState?.countries?.length) {
      filtered = filtered?.filter((p) => p.active?.some((country) => filterState?.countries.includes(country)));
    }

    if (filterState?.flows?.length) {
      filtered = filtered?.filter((p) => filterState?.flows.includes(p.flow));
    }

    return filtered;
  }, [procedures, search, filterState]);

  const list = useMemo(() => {
    let result = filteredProcedures;

    if (orderBy) {
      result = stableSort(
        result,
        getComparator(order, orderBy)
      );
    }

    const _page = page - 1;

    return result
      .slice(
        _page * rowsPerPage,
        _page * rowsPerPage + rowsPerPage
      );
  }, [filteredProcedures, page, rowsPerPage, order, orderBy]);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      {fetching && <LoadScreen />}
      <Box className="cases-table-container">
        <Box className="table-title-container">
          <Box className="flex-1">
            <Box className="d-flex">
              {withButton && (
                <Box sx={{ display: { sm: 'none' } }}>
                  <Box className="m-r-sm">
                    <MuiIconButton
                      edge="start"
                      color="primary"
                      aria-label="menu"
                      onClick={toggleSidebar}
                    >
                      <MenuIcon />
                    </MuiIconButton>
                  </Box>
                </Box>
              )}

              <Box>
                <Box className="table-title">
                  Procedures
                  <Box className="badge">{procedures.length}</Box>
                </Box>

                {!isMobile && (
                  <Box className='table-subtitle'>
                    Add or manage procedures here
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          <Box className='d-flex'>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '240px', mr: 2 }}>
              <Search
                value={search}
                placeholder="Search"
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
              />
            </Box>
            <IconButton
              onClick={() => toggleFilterModal(true)}
              icon={<FilterIcon />}
              sx={{ mr: 2 }}
            />
            <Button
              text='Add Procedure'
              onClick={() => toggleCreateModal(true)}
              icon={<AddIcon color='white' />}
            />
          </Box>
        </Box>
        {procedures.length ? (
          <Fragment>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              {filterState &&
                (Object.keys(filterState)?.some((key) => filterState[key]?.length)
                  && (
                    <div className="filter-list">
                      <FiltersIcon color="secondary" />
                      {Object.keys(filterState).map((key) => (
                        <div key={key} className="filter-list-item-container">
                          {filterState[key].map((value) => (
                            <div key={`${key}-${value}`} className="filter-list-item">
                              <div />
                              <div className="filter-list-item-value">{value}</div>
                              <CloseIcon color="secondary" onClick={() => onRemoveFilterItem(key, value)} />
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))
              }
              <ProceduresTable
                procedures={list}
                distributors={distributors}
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalRows={filteredProcedures?.length}
                order={order}
                setOrder={setOrder}
                orderBy={orderBy}
                setOrderBy={setOrderBy}
              />
            </Box>

            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <ProceduresTableMobile procedures={list} />
            </Box>
          </Fragment>
        ) : (
          <Box className="empty-block">No Procedures</Box>
        )}
      </Box>

      <ConfirmationModal
        open={showDeleteModal}
        onClose={() => toggleDeleteModal(false)}
        onSubmit={onDeleteProcedure}
        title="Delete Procedure"
        text="Are you sure you want to delete this procedure?"
        cancelText="Cancel"
        submitText="Delete"
        loading={loading}
        variant="error"
      />

      <ProcedureModal
        open={showCreateModal}
        onClose={() => toggleCreateModal(false)}
        onSubmit={onProcedureCreate}
        countries={countries}
        loading={loading}
        productsList={products}
        documentsList={documents}
        onUpload={onUploadProducts}
        missingProducts={missingProducts}
      />

      <ProcedureModal
        open={showUpdateModal}
        onClose={() => {
          toggleUpdateModal(false);
          setSelectedProcedure(undefined);
        }}
        onSubmit={onProcedureUpdate}
        countries={countries}
        mode='update'
        initialValues={selectedProcedure}
        loading={loading}
        productsList={products}
        documentsList={documents}
        onUpload={onUploadProducts}
        missingProducts={missingProducts}
      />

      <FilterProceduresModal
        filter={filterState}
        open={showFilterModal}
        onClose={() => toggleFilterModal(false)}
        onSubmit={onSubmitFilters}
        countries={countries}
        procedures={procedures}
      />

      <ConfirmationModal
        open={showDeleteModal}
        onClose={() => toggleDeleteModal(false)}
        onSubmit={onDeleteProcedure}
        title="Delete Procedure?"
        text="Are you sure you want to delete this procedure? This action cannot be reversed."
        closeText="Cancel"
        submitText="Delete"
        variant="error"
        loading={deleteLoading}
      />

      <Alert
        variant="success"
        message={successMessage}
        open={!!successMessage}
        onClose={() => setSuccessMessage('')}
      />
    </>
  );
};

ProceduresTableContainer.propTypes = {
  procedures: PropTypes.array,
  distributors: PropTypes.array,
};

const mapStateToProps = (state) => {
  return {
    countries: state.countries.list,
    procedureTags: state.procedures.tags,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchProcedures: () => dispatch(fetchProcedures()),
    createProcedure: (procedureData) => dispatch(createProcedure(procedureData)),
    updateProcedure: (id, procedureData) => dispatch(updateProcedure(id, procedureData)),
    deleteProcedure: (id) => dispatch(deleteProcedure(id)),
    toggleSidebar: () => dispatch(toggleSidebar()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProceduresTableContainer);
